<script lang="ts">
	/**
	 * The one filter set, driving every panel on the reports page (Design §13).
	 *
	 * It is the analytics bar's sibling and follows its rules deliberately, because the failure it
	 * prevents is the same one and worse here: a page whose panels each read their own filters is a
	 * page whose panels disagree about what is on screen, and on a report of **money** every one of
	 * them still renders a plausible amount that somebody then quotes.
	 *
	 * So: each control is labelled through `FormField` and the controls are grouped, because the
	 * grouping is half the label; anything off its "everything" value carries an accent ring and the
	 * number in force is stated beside Clear; the two date fields are always on screen rather than
	 * behind a Custom button, so the bar never changes height as it is used and a saved link
	 * carrying dates no preset matches still says what it is drawing.
	 *
	 * **One control is this page's own, and it is the most important on the bar.** The kind filter
	 * is a set of toggles rather than a dropdown, because the questions an operator actually asks —
	 * "revenue", "money back out" — are each *several* kinds, and a control offering one at a time
	 * would make the commonest reading of the page impossible to produce. Nothing selected means
	 * everything, which is what an unfiltered ledger is; the two shortcuts beside them are named
	 * after the questions rather than after the kinds they select.
	 */

	import { createEventDispatcher } from 'svelte';
	import { Button } from 'flowbite-svelte';
	import { CurrencyPicker, FormField, SelectField } from '../../../components';
	import { currencyRowsOf } from '../../../stores/storefront';
	import type { ReportEventKind, ReportFilterOptions, ReportQuery } from '../../../AppTypes';

	export let filter: ReportQuery;
	export let options: ReportFilterOptions | null = null;
	export let busy: boolean = false;

	/**
	 * Which kinds of movement this page can be narrowed to.
	 *
	 * A prop rather than the constant it was, because the bar now drives two screens and one of them
	 * cannot express two of the five: the invoice register lists invoices, and a refund is not an
	 * invoice. Offering a toggle that always returns nothing is worse than offering none — an empty
	 * table reads as "there are none of these", which on a page about money is a wrong answer rather
	 * than a missing control.
	 *
	 * The two shortcuts follow it: each renders only where every kind it selects is on offer, since
	 * a "Money out" button that half-applies is a filter nobody can describe.
	 */
	export let kinds: ReportEventKind[] = ['sale', 'renewal', 'adjustment', 'refund', 'chargeback'];
	/** The chart's bucket size, which a page with no chart on it has no use for. */
	export let showGranularity: boolean = true;
	/** What the kind group is called. On the register they are what an invoice was *for*. */
	export let kindLabel: string = 'What moved';
	/**
	 * Filters the host page adds through the slot, counted in the "narrowing this report" total.
	 *
	 * The count is the thing that says "this is not the whole store", so a slotted control missing
	 * from it makes the bar under-report exactly when somebody is reading a narrowed figure.
	 */
	export let extraActive: number = 0;

	const dispatcher = createEventDispatcher<{ change: ReportQuery }>();

	/**
	 * The kinds, in the order money moves: in, in again, adjusted, out, taken.
	 *
	 * Each carries its own sentence because the difference between a sale and a renewal is the whole
	 * point of splitting them, and a row of five bare nouns invites somebody to read `adjustment` as
	 * a rounding correction rather than as what a plan change bills.
	 */
	const ALL_KINDS: { value: ReportEventKind; name: string; help: string }[] = [
		{ value: 'sale', name: 'Sales', help: 'A first purchase — a new subscription or a one-time order.' },
		{ value: 'renewal', name: 'Renewals', help: 'A subscription billing again because the period came round.' },
		{ value: 'adjustment', name: 'Adjustments', help: 'What a plan or seat change billed, which may be a credit.' },
		{ value: 'refund', name: 'Refunds', help: 'Money given back, with its credit note.' },
		{ value: 'chargeback', name: 'Chargebacks', help: 'Money taken back through a dispute.' }
	];

	/** The two questions the page is actually opened with, each of which is several kinds. */
	const MONEY_IN: ReportEventKind[] = ['sale', 'renewal', 'adjustment'];
	const MONEY_OUT: ReportEventKind[] = ['refund', 'chargeback'];

	$: offeredKinds = ALL_KINDS.filter(kind => kinds.includes(kind.value));
	/** A shortcut renders only where it can be honoured in full. See the `kinds` prop. */
	$: showMoneyIn = MONEY_IN.every(kind => kinds.includes(kind));
	$: showMoneyOut = MONEY_OUT.every(kind => kinds.includes(kind));

	const RANGES: { label: string; days: number }[] = [
		{ label: 'Last 7 days', days: 7 },
		{ label: 'Last 30 days', days: 30 },
		{ label: 'Last 90 days', days: 90 },
		{ label: 'Last 12 months', days: 365 }
	];

	/**
	 * The bucket a range of this length should be drawn at.
	 *
	 * A year by day is 365 points in a 280px box, which is a smear rather than a trend.
	 */
	function granularityFor(days: number): 'day' | 'week' | 'month' {
		return days > 180 ? 'month' : days > 45 ? 'week' : 'day';
	}

	function fnRange(days: number): void {
		const to = new Date();
		const from = new Date(to.getTime() - days * 86400000);
		dispatcher('change', {
			...filter,
			from: from.toISOString(),
			to: to.toISOString(),
			granularity: granularityFor(days)
		});
	}

	/** `YYYY-MM-DD` in UTC, which is what a date input reads and writes. */
	function dayOf(iso: string | undefined): string {
		if (iso === undefined) return '';
		const parsed = new Date(iso);
		return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().slice(0, 10);
	}

	$: fromDay = dayOf(filter.from);
	$: toDay = dayOf(filter.to);
	$: todayDay = new Date().toISOString().slice(0, 10);

	/**
	 * Apply one end of the custom range.
	 *
	 * **The end date is taken to the end of its day.** `new Date('2026-06-30')` is midnight UTC, so
	 * a range ending on the 30th built from it excludes everything that happened on the 30th — the
	 * operator picks a day and the day's takings are missing, which reads as lost sales rather than
	 * as an off-by-one. On a financial report that is a figure somebody reconciles against a bank
	 * statement and cannot make add up.
	 */
	function fnCustom(edge: 'from' | 'to', day: string): void {
		if (day.length === 0) return;
		const next = {
			...filter,
			[edge]: new Date(`${day}T${edge === 'from' ? '00:00:00.000' : '23:59:59.999'}Z`).toISOString()
		};
		if (new Date(next.from ?? '').getTime() > new Date(next.to ?? '').getTime()) {
			return;
		}
		const span =
			(new Date(next.to ?? '').getTime() - new Date(next.from ?? '').getTime()) / 86400000;
		dispatcher('change', { ...next, granularity: granularityFor(span) });
	}

	/**
	 * A `<select>`'s new value.
	 *
	 * A helper rather than a cast in the markup: Svelte's template expressions are plain JavaScript,
	 * so a TypeScript `as` there fails the build with an "unexpected token" mid-attribute.
	 */
	function fnValueOf(event: Event): string {
		return (event.target as HTMLInputElement).value;
	}

	/** The same for a `SelectField` or a `CurrencyPicker`, which report the row rather than an event. */
	function fnChosen(event: CustomEvent<{ value: string }>): string {
		return event.detail.value;
	}

	function fnSet(key: keyof ReportQuery, value: string): void {
		dispatcher('change', { ...filter, [key]: value === '' ? undefined : value });
	}

	/**
	 * The amount threshold, in **major** units as an admin types them.
	 *
	 * Converted to minor units against a flat hundred rather than a currency's own
	 * `minorUnitDigits`, and that is a compromise stated rather than hidden: the filter spans every
	 * currency at once, so there is no single row to read the digits off. It is a coarse "show me
	 * the large movements" control and the help text says so — a figure that has to be exact goes
	 * through the search box or the export, both of which state amounts in minor units.
	 */
	function fnMinAmount(value: string): void {
		const parsed = Number.parseFloat(value.replace(',', '.'));
		dispatcher('change', {
			...filter,
			minAmount:
				Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed * 100) : undefined
		});
	}

	$: minAmountText =
		filter.minAmount === undefined ? '' : String(filter.minAmount / 100);

	/** Which kinds are in force. Empty is every kind, which is what an unfiltered ledger is. */
	$: selectedKinds = filter.kind ?? [];

	function fnToggleKind(kind: ReportEventKind): void {
		const next = selectedKinds.includes(kind)
			? selectedKinds.filter(entry => entry !== kind)
			: [...selectedKinds, kind];
		dispatcher('change', { ...filter, kind: next.length === 0 ? undefined : next });
	}

	/**
	 * A shortcut, and it **toggles rather than sets**.
	 *
	 * Pressing "Money in" when money in is already the whole selection clears it back to every
	 * kind, so the control that narrowed the page is the control that widens it. A shortcut that
	 * only ever narrowed would leave Clear as the single way out of it, which puts the way back on
	 * the other side of the bar from the way in.
	 */
	function fnShortcut(kinds: ReportEventKind[]): void {
		const same =
			selectedKinds.length === kinds.length && kinds.every(kind => selectedKinds.includes(kind));
		dispatcher('change', { ...filter, kind: same ? undefined : kinds });
	}

	function fnIsShortcut(kinds: ReportEventKind[], selected: ReportEventKind[]): boolean {
		return selected.length === kinds.length && kinds.every(kind => selected.includes(kind));
	}

	/**
	 * Which preset is in force, or `null` for a range none of them describes.
	 *
	 * Matched rather than snapped to the nearest: snapping lights "Last 7 days" for a hand-picked
	 * 1–14 March, which is a statement about the figures on screen and a wrong one.
	 */
	$: activeDays = (() => {
		if (filter.from === undefined || filter.to === undefined) return null;
		const to = new Date(filter.to).getTime();
		const from = new Date(filter.from).getTime();
		if (Number.isNaN(to) || Number.isNaN(from)) return null;
		if (new Date(to).toISOString().slice(0, 10) !== todayDay) return null;
		const span = (to - from) / 86400000;
		return RANGES.find(range => Math.abs(range.days - span) < 1)?.days ?? null;
	})();

	/** A `<SelectField>`'s items, with a leading "everything" row that clears the filter. */
	function fnItems(values: string[], anyLabel: string): { value: string; name: string }[] {
		return [{ value: '', name: anyLabel }, ...values.map(value => ({ value, name: value }))];
	}

	/**
	 * The ring on a control that is narrowing something.
	 *
	 * Whole literal class strings per branch, never an interpolated fragment: Tailwind's scanner
	 * reads source text, so a class assembled at runtime is invisible to it and the rule silently
	 * never ships.
	 */
	function fieldClass(active: boolean): string {
		return active ? '!border-primary-500 ring-1 ring-primary-500' : '';
	}

	/**
	 * How many filters are narrowing the page.
	 *
	 * A count rather than a boolean: "this is not the whole store" is a different statement from
	 * "one thing is filtered", and somebody reading a revenue figure needs to know which. The range
	 * is excluded, because every view has one.
	 */
	$: activeFilters = [
		selectedKinds.length === 0 ? undefined : 'kind',
		filter.currency,
		filter.productUUID,
		filter.familySlug,
		filter.userUUID,
		filter.country,
		filter.sellerUUID,
		filter.providerUUID,
		filter.couponCode,
		filter.minAmount === undefined ? undefined : 'minAmount',
		filter.search
	].filter(value => value !== undefined && value !== '').length + extraActive;

	function fnClear(): void {
		dispatcher('change', {
			from: filter.from,
			to: filter.to,
			granularity: filter.granularity,
			sort: filter.sort
		});
	}
</script>

<div class="widget bg-depth space-y-4">
	<!-- When ────────────────────────────────────────────────────────────────────────────── -->
	<div class="flex flex-wrap items-end gap-x-4 gap-y-2">
		<div class="min-w-0">
			<div class="card-label mb-1.5">
				<i class="fa-solid fa-calendar-days text-[11px]" />Period
			</div>
			<div class="flex flex-wrap items-center gap-1.5">
				{#each RANGES as range (range.days)}
					<Button
						size="xs"
						color={activeDays === range.days ? 'primary' : 'alternative'}
						disabled={busy}
						on:click={() => fnRange(range.days)}
					>
						{range.label}
					</Button>
				{/each}
			</div>
		</div>

		<!--
			Each field bounds the other, and neither may pass today: a range that runs backwards
			reports nothing with no explanation, and one ending in the future draws a flat tail that
			reads as sales having stopped.
		-->
		<div class="flex items-end gap-2">
			<div>
				<label class="card-label mb-1" for="reports-from">From</label>
				<input
					id="reports-from"
					type="date"
					class="border border-input bg-depth-current rounded-lg text-sm p-1.5 focus:border-primary-500 focus:ring-primary-500"
					value={fromDay}
					max={toDay.length > 0 ? toDay : todayDay}
					disabled={busy}
					on:change={event => fnCustom('from', fnValueOf(event))}
				/>
			</div>
			<span class="text-default text-xs pb-2">to</span>
			<div>
				<label class="card-label mb-1" for="reports-to">To</label>
				<input
					id="reports-to"
					type="date"
					class="border border-input bg-depth-current rounded-lg text-sm p-1.5 focus:border-primary-500 focus:ring-primary-500"
					value={toDay}
					min={fromDay}
					max={todayDay}
					disabled={busy}
					on:change={event => fnCustom('to', fnValueOf(event))}
				/>
			</div>
		</div>

		{#if showGranularity}
		<div class="ml-auto w-32">
			<FormField label="Group by" id="reports-granularity">
				<SelectField
					id="reports-granularity"
					size="sm"
					value={filter.granularity ?? 'day'}
					on:change={event => fnSet('granularity', fnChosen(event))}
					items={[
						{ value: 'day', name: 'Daily' },
						{ value: 'week', name: 'Weekly' },
						{ value: 'month', name: 'Monthly' }
					]}
				/>
			</FormField>
		</div>
		{/if}
	</div>

	<!-- What moved ──────────────────────────────────────────────────────────────────────── -->
	<div class="pt-3 border-t border-widget-color">
		<div class="card-label mb-2">
			<i class="fa-solid fa-arrow-right-arrow-left text-[11px]" />{kindLabel}
		</div>
		<div class="flex flex-wrap items-center gap-1.5">
			{#each offeredKinds as kind (kind.value)}
				<Button
					size="xs"
					color={selectedKinds.includes(kind.value) ? 'primary' : 'alternative'}
					disabled={busy}
					title={kind.help}
					on:click={() => fnToggleKind(kind.value)}
				>
					{kind.name}
				</Button>
			{/each}
			{#if showMoneyIn || showMoneyOut}
				<span class="text-default text-xs mx-1">or</span>
			{/if}
			{#if showMoneyIn}
				<Button
					size="xs"
					color={fnIsShortcut(MONEY_IN, selectedKinds) ? 'primary' : 'alternative'}
					disabled={busy}
					title="Sales, renewals and adjustments together — everything that brought money in."
					on:click={() => fnShortcut(MONEY_IN)}
				>
					Money in
				</Button>
			{/if}
			{#if showMoneyOut}
				<Button
					size="xs"
					color={fnIsShortcut(MONEY_OUT, selectedKinds) ? 'primary' : 'alternative'}
					disabled={busy}
					title="Refunds and chargebacks together — everything that took money back out."
					on:click={() => fnShortcut(MONEY_OUT)}
				>
					Money out
				</Button>
			{/if}
			<!-- Stated rather than left to be inferred from five unlit buttons, which is exactly the
			     shape of "the filter is off" that reads as "the filter is broken". -->
			{#if selectedKinds.length === 0}
				<span class="text-default text-xs ml-1">Every kind of movement.</span>
			{/if}
		</div>
	</div>

	<!-- What sold ───────────────────────────────────────────────────────────────────────── -->
	<div class="pt-3 border-t border-widget-color">
		<div class="card-label mb-2"><i class="fa-solid fa-box-open text-[11px]" />What sold</div>
		<div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
			<FormField label="Product family" id="reports-family">
				<SelectField
					id="reports-family"
					size="sm"
					class={fieldClass(filter.familySlug !== undefined)}
					value={filter.familySlug ?? ''}
					on:change={event => fnSet('familySlug', fnChosen(event))}
					items={[
						{ value: '', name: 'Every family' },
						...(options?.families ?? []).map(family => ({
							value: family.slug,
							name: family.name
						}))
					]}
				/>
			</FormField>

			<FormField label="Product" id="reports-product">
				<SelectField
					id="reports-product"
					size="sm"
					class={fieldClass(filter.productUUID !== undefined)}
					value={filter.productUUID ?? ''}
					on:change={event => fnSet('productUUID', fnChosen(event))}
					items={[
						{ value: '', name: 'Every product' },
						...(options?.products ?? []).map(product => ({
							value: product.uuid,
							name: product.name
						}))
					]}
				/>
			</FormField>

			<FormField
				label="Billed in"
				id="reports-currency"
				helpText="The currency the money actually moved in. Every figure on this page is per currency and nothing is ever summed across them — minor units in two currencies are different units."
			>
				<CurrencyPicker
					id="reports-currency"
					label="Billed in"
					compact
					class={fieldClass(filter.currency !== undefined)}
					currencies={$currencyRowsOf(options?.currencies ?? [])}
					value={filter.currency ?? ''}
					anyLabel="Every currency"
					on:change={event => fnSet('currency', fnChosen(event))}
				/>
			</FormField>
		</div>
	</div>

	<!-- Who paid ────────────────────────────────────────────────────────────────────────── -->
	<div class="pt-3 border-t border-widget-color">
		<div class="card-label mb-2"><i class="fa-solid fa-users text-[11px]" />Who paid</div>
		<div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
			<FormField
				label="Country"
				id="reports-country"
				helpText="The customer's billing country, which is what tax was charged against — not where they happened to be browsing from."
			>
				<SelectField
					id="reports-country"
					size="sm"
					class={fieldClass(filter.country !== undefined)}
					value={filter.country ?? ''}
					on:change={event => fnSet('country', fnChosen(event))}
					items={fnItems(options?.countries ?? [], 'Everywhere')}
				/>
			</FormField>

			<FormField
				label="Coupon"
				id="reports-coupon"
				helpText="The discount code the purchase was made under, where there was one."
			>
				<SelectField
					id="reports-coupon"
					size="sm"
					class={fieldClass(filter.couponCode !== undefined)}
					value={filter.couponCode ?? ''}
					on:change={event => fnSet('couponCode', fnChosen(event))}
					items={fnItems(options?.coupons ?? [], 'With or without')}
				/>
			</FormField>

			<FormField
				label="Search"
				id="reports-search"
				helpText="Matches the customer, their email, the product and the document reference — so an invoice number off a piece of paper finds its row."
			>
				<input
					id="reports-search"
					type="search"
					placeholder="Name, email, product or reference"
					class="w-full border border-input bg-depth-current rounded-lg text-sm p-1.5 focus:border-primary-500 focus:ring-primary-500 {fieldClass(filter.search !== undefined)}"
					value={filter.search ?? ''}
					disabled={busy}
					on:change={event => fnSet('search', fnValueOf(event))}
				/>
			</FormField>
		</div>
	</div>

	<!-- Who sold it ─────────────────────────────────────────────────────────────────────── -->
	<div class="pt-3 border-t border-widget-color">
		<div class="card-label mb-2"><i class="fa-solid fa-building text-[11px]" />Who sold it</div>
		<div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
			<FormField
				label="Seller"
				id="reports-seller"
				helpText="The legal entity the document was issued by. A store with more than one files a return for each."
			>
				<SelectField
					id="reports-seller"
					size="sm"
					class={fieldClass(filter.sellerUUID !== undefined)}
					value={filter.sellerUUID ?? ''}
					on:change={event => fnSet('sellerUUID', fnChosen(event))}
					items={[
						{ value: '', name: 'Every seller' },
						...(options?.sellers ?? []).map(seller => ({
							value: seller.uuid,
							name: seller.name
						}))
					]}
				/>
			</FormField>

			<FormField label="Payment provider" id="reports-provider">
				<SelectField
					id="reports-provider"
					size="sm"
					class={fieldClass(filter.providerUUID !== undefined)}
					value={filter.providerUUID ?? ''}
					on:change={event => fnSet('providerUUID', fnChosen(event))}
					items={[
						{ value: '', name: 'Every provider' },
						...(options?.providers ?? []).map(provider => ({
							value: provider.uuid,
							name: provider.name
						}))
					]}
				/>
			</FormField>

			<FormField
				label="At least"
				id="reports-min-amount"
				helpText="Movements of at least this size, in whole units of whichever currency the row is in. It compares the size rather than the signed amount, so a threshold does not silently drop every refund."
			>
				<input
					id="reports-min-amount"
					type="text"
					inputmode="decimal"
					placeholder="Any amount"
					class="w-full border border-input bg-depth-current rounded-lg text-sm p-1.5 focus:border-primary-500 focus:ring-primary-500 {fieldClass(filter.minAmount !== undefined)}"
					value={minAmountText}
					disabled={busy}
					on:change={event => fnMinAmount(fnValueOf(event))}
				/>
			</FormField>
		</div>
	</div>

	<!--
		Whatever the host page adds. On the register it is the invoice's own status, which the ledger
		has no concept of — it is inside the bar rather than in a control panel above it so that one
		Clear reaches every filter and the count below speaks for all of them.
	-->
	<slot name="filters" />

	{#if activeFilters > 0}
		<div class="pt-3 border-t border-widget-color flex flex-wrap items-center gap-3">
			<!-- The count is the point: "this is not the whole store" is what a reader of a revenue
			     figure has to know, and a Clear button alone does not say it. -->
			<span class="text-sm text-default">
				{activeFilters} filter{activeFilters === 1 ? '' : 's'} narrowing this report.
			</span>
			<Button size="xs" color="alternative" disabled={busy} on:click={fnClear}>Clear filters</Button>
		</div>
	{/if}
</div>
